/** Dettaglio di un mercato: orari completi, titoli collegati, storico briefing (SPEC-010 §8.2). */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, EmptyState, Select, Spinner, StatCard, Switch, TableWrap } from '@/ui';
import { AgenticBadge } from '@/features/agent-runs/AgenticBadge';
import { useWishlist } from '@/features/wishlist/api';
import { MarketTimeline } from './MarketTimeline';
import { RequestBriefingButton } from './RequestBriefingButton';
import {
  useMarketDetail,
  useSetMarketTrigger,
  type EndOfDayMode,
  type MarketDetail,
  type MarketSchedule,
  type OpenMode,
  type TriggerPatch,
} from './api';
import {
  BRIEFING_STATUS_LABELS,
  BRIEFING_STATUS_TONE,
  formatCountdown,
  nextEventFor,
  STATUS_LABELS,
  STATUS_TONE,
} from './market-status-format';
import './markets.css';

const SCHEDULE_ROWS: Array<{ label: string; key: keyof MarketSchedule }> = [
  { label: 'Pre-market', key: 'preMarketOpen' },
  { label: 'Asta di apertura', key: 'openingAuctionStart' },
  { label: 'Apertura continua', key: 'continuousOpen' },
  { label: 'Pausa pranzo (inizio)', key: 'lunchStart' },
  { label: 'Pausa pranzo (fine)', key: 'lunchEnd' },
  { label: 'Asta di chiusura', key: 'closingAuctionStart' },
  { label: 'Chiusura continua', key: 'continuousClose' },
  { label: 'After-hours (fine)', key: 'afterHoursClose' },
];

const END_OF_DAY_MODE_OPTIONS: Array<{ value: EndOfDayMode; label: string }> = [
  { value: 'off', label: 'Spenta' },
  { value: 'standard', label: 'Standard — una chiamata, aggiorna il digest' },
  { value: 'agentic', label: 'Agentica — studio con gli strumenti, propone da sé' },
];

const OPEN_MODE_OPTIONS: Array<{ value: OpenMode; label: string }> = [
  { value: 'standard', label: 'Standard — analisi TradingAgents su tutti i titoli, poi il riepilogo' },
  { value: 'agentic_deferred', label: 'Agentico con analisi differita — lo studio sceglie al massimo 3 analisi' },
];

const BRIEFING_KIND_LABELS: Record<string, string> = {
  open: 'apertura',
  pre_close: 'pre-chiusura',
  after_hours: 'after-hours',
};

/**
 * Inneschi di fine giornata (SPEC-017): la revisione che rilegge le analisi del
 * mattino alla luce di com'è andata la seduta, prima che sia troppo tardi per
 * agire.
 *
 * Tre scelte di interfaccia che non sono estetiche:
 *  - i controlli restano **visibili e disabilitati** quando il trigger di
 *    apertura è spento, con la ragione scritta accanto. Nasconderli renderebbe
 *    la dipendenza invisibile, e l'utente cercherebbe una funzione che c'è;
 *  - l'after-hours **non compare affatto** dove la borsa non lo negozia: lì non
 *    è una condizione da soddisfare, è una fase che non esiste;
 *  - la modalità è **una tendina a tre voci** e non un interruttore più una
 *    spunta (F-144): «agentica» non è un'opzione in più sopra la revisione
 *    standard, è un altro modo di farla, e le due non convivono sulla stessa
 *    fase. Il server la rifiuta con 422 se il runtime agentico è spento: il
 *    messaggio si mostra qui, sotto ai controlli, dove si è fatta la scelta.
 */
function EndOfDayTriggers({
  market,
  pending,
  error,
  onChange,
}: {
  market: MarketDetail;
  pending: boolean;
  error: unknown;
  onChange: (patch: TriggerPatch) => void;
}) {
  const blocked = !market.triggerFeatureEnabled || !market.triggerEnabled;
  // SPEC-026 §7.3: un mercato sempre aperto non chiude. La «pre-chiusura» è
  // una revisione serale all'ora scelta qui, obbligatoria per accenderla.
  const [reviewTime, setReviewTime] = useState(market.reviewTimeLocal ?? '18:30');

  return (
    <Card
      title="Inneschi di fine giornata"
      subtitle={
        market.trades24x7
          ? "Il mercato non chiude: la revisione serale rilegge le analisi del mattino insieme a com'è andata la giornata, all'ora che scegli. Non rifà le analisi."
          : "Prima che la seduta chiuda, rilegge le analisi del mattino insieme a com'è andata davvero la giornata: prezzo, operazioni fatte o rifiutate, notizie. Non rifà le analisi."
      }
    >
      {blocked && (
        <Alert variant="info" title="Serve prima il trigger di apertura">
          La revisione di fine giornata rilegge le analisi che il trigger di apertura produce: senza
          quelle, non avrebbe niente da rileggere.
        </Alert>
      )}
      {/* Incolonnati e non affiancati: sono due fasi indipendenti, e una sotto
          l'altra si leggono come tali. */}
      <div className="markets__switch-list">
        {market.trades24x7 && (
          <label className="markets__review-time">
            <span>Ora della revisione serale</span>
            <input
              type="time"
              value={reviewTime}
              aria-label="Ora della revisione serale"
              onChange={(event) => setReviewTime(event.target.value)}
              onBlur={() => reviewTime && reviewTime !== market.reviewTimeLocal && onChange({ reviewTimeLocal: reviewTime })}
              disabled={blocked || pending}
            />
            <span className="cell--minor">({market.timezoneIana})</span>
          </label>
        )}
        <Select
          label={market.trades24x7 ? 'Revisione serale' : 'Revisione pre-chiusura'}
          value={market.preCloseMode}
          options={END_OF_DAY_MODE_OPTIONS}
          onChange={(event) =>
            onChange({
              preCloseMode: event.target.value as EndOfDayMode,
              // Sul 24/7 l'ora viaggia con la modalità: il server la esige per accendere.
              ...(market.trades24x7 && event.target.value !== 'off' ? { reviewTimeLocal: reviewTime } : {}),
            })
          }
          disabled={blocked || pending}
        />
        {market.supportsAfterHours && (
          <Select
            label="Revisione after-hours"
            value={market.afterHoursMode}
            options={END_OF_DAY_MODE_OPTIONS}
            onChange={(event) => onChange({ afterHoursMode: event.target.value as EndOfDayMode })}
            disabled={blocked || pending}
          />
        )}
      </div>
      {(market.preCloseMode === 'agentic' || market.afterHoursMode === 'agentic') && (
        <p className="markets__mode-hint">
          <AgenticBadge />{' '}
          Lo studio parte alla stessa ora della revisione standard, con lo stesso contesto di
          chiusura, ma il modello può interrogare quotazioni, analisi, notizie e vincoli prima di
          concludere. Può proporre vendite sulle posizioni aperte di questo mercato e acquisti sui
          titoli in wishlist: ogni proposta passa comunque dal cancello, dai limiti e dalla verifica
          del motore. Prompt, ragionamento e chiamate restano consultabili nel run.
        </p>
      )}
      {error instanceof ApiError && (
        <Alert variant="error" title="Modalità non applicata">
          {error.message}
        </Alert>
      )}
    </Card>
  );
}

export function MarketDetailPage() {
  const { mic } = useParams<{ mic: string }>();
  const detailQuery = useMarketDetail(mic ?? '', Boolean(mic));
  const setTrigger = useSetMarketTrigger(mic ?? '');
  // Wishlist di TUTTI i portafogli: qui non si guarda un portafoglio, si guarda
  // un mercato, e la domanda è «su quali di questi titoli ho già deciso di
  // investire, ovunque».
  //
  // **Prima dei return anticipati**: sotto ci sono i rami di caricamento ed
  // errore, e un hook dichiarato dopo verrebbe eseguito solo in alcuni render —
  // «Rendered more hooks than during the previous render». Il typecheck non se
  // ne accorge, i test sì.
  const wishlist = useWishlist(null);
  const wishlistByInstrument = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const item of wishlist.data?.items ?? []) {
      if (item.expired) continue;
      const names = map.get(item.instrument.id) ?? [];
      names.push(item.portfolio.name);
      map.set(item.instrument.id, names);
    }
    return map;
  }, [wishlist.data]);

  if (detailQuery.isPending) {
    return (
      <div className="markets">
        <Spinner label="Caricamento del mercato…" />
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="markets">
        <Alert variant="error" title="Impossibile caricare il mercato">
          {detailQuery.error instanceof ApiError ? detailQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      </div>
    );
  }

  const market = detailQuery.data;
  const event = nextEventFor(market);
  // Una mutazione sola per tutti gli inneschi: il rifiuto si mostra nella card
  // in cui si è fatta la scelta, non in entrambe.
  const openModeError = setTrigger.variables?.openMode !== undefined ? setTrigger.error : null;
  const endOfDayError = setTrigger.variables?.openMode === undefined ? setTrigger.error : null;

  return (
    <div className="markets">
      <header className="markets__header">
        <div>
          <Link to="/markets" className="markets__back">
            ← Torna ai mercati
          </Link>
          <h1 className="markets__title">{market.name}</h1>
          <p className="markets__subtitle">
            {market.mic} · {market.city} · {market.currency} · {market.timezoneIana}
          </p>
        </div>
        <div className="markets__header-actions">
          <RequestBriefingButton
            mic={market.mic}
            name={market.name}
            watchlistInstrumentCount={market.watchlistInstrumentCount}
          />
          <Badge tone={STATUS_TONE[market.status]}>{STATUS_LABELS[market.status]}</Badge>
        </div>
      </header>

      <div className="portfolios__stats">
        <StatCard label="Stato attuale" value={STATUS_LABELS[market.status]} />
        {event ? (
          <StatCard label={event.label} value={formatCountdown(event.at)} hint={formatDateTime(event.at)} />
        ) : (
          <StatCard label="Sessione" value="Sempre aperto" hint="Nessun weekend, nessuna festività" />
        )}
        <StatCard label="Titoli in watchlist collegati" value={market.watchlistInstrumentCount} />
      </div>

      {/* Sopra la configurazione del trigger e sopra l'orario tabellare: la
          domanda «che cosa succede adesso e fra poco» è quella per cui si apre
          questa pagina, e le altre due la spiegano invece di rispondere. */}
      <MarketTimeline mic={market.mic} timezoneIana={market.timezoneIana} />

      <Card
        title={market.briefingTimeLocal ? 'Resoconto giornaliero' : 'Trigger pre-apertura'}
        subtitle={
          market.briefingTimeLocal
            ? `Ogni giorno alle ${market.briefingTimeLocal} (${market.timezoneIana}), avvia l'analisi AI sui titoli in watchlist collegati a questo mercato e invia un riepilogo aggregato`
            : "Un'ora prima dell'apertura, avvia l'analisi AI sui titoli in watchlist collegati a questo mercato e invia un riepilogo aggregato"
        }
      >
        {!market.triggerFeatureEnabled && (
          <Alert variant="info" title="Disattivato per l'intero server">
            La funzionalità è spenta a livello di configurazione (<code>MARKET_OPEN_TRIGGER_ENABLED</code>):
            l&apos;interruttore per questo mercato non avrebbe effetto.
          </Alert>
        )}
        <Switch
          label={market.triggerFeatureEnabled ? (market.triggerEnabled ? 'Attivo' : 'Disattivo') : 'Non disponibile sul server'}
          checked={market.triggerEnabled}
          onChange={(checked) => setTrigger.mutate({ enabled: checked })}
          disabled={!market.triggerFeatureEnabled}
        />
        {/* F-199: la modalità resta sceglibile a trigger spento — vale anche
            per il resoconto chiesto a mano con «Resoconto ora». */}
        <div className="markets__switch-list">
          <Select
            label="Modalità del resoconto"
            value={market.openMode}
            options={OPEN_MODE_OPTIONS}
            onChange={(event) => setTrigger.mutate({ openMode: event.target.value as OpenMode })}
            disabled={setTrigger.isPending}
          />
        </div>
        {market.openMode === 'agentic_deferred' && (
          <p className="markets__mode-hint">
            <AgenticBadge />{' '}
            Il resoconto parte subito come studio agentico, senza lanciare prima TradingAgents su ogni
            titolo. Il modello legge quotazioni, notizie e analisi passate e chiede un&apos;analisi
            approfondita solo dove serve, al massimo su 3 titoli; poi riprende una volta sola quando
            sono concluse, o comunque in tempo per l&apos;apertura. Se non serve nessuna analisi, il
            resoconto si chiude subito. Digest e auto-trading partono solo se almeno un&apos;analisi
            è conclusa.
          </p>
        )}
        {openModeError instanceof ApiError && (
          <Alert variant="error" title="Modalità non applicata">
            {openModeError.message}
          </Alert>
        )}
      </Card>

      <EndOfDayTriggers
        market={market}
        pending={setTrigger.isPending}
        error={endOfDayError}
        onChange={setTrigger.mutate}
      />

      <Card
        title="Orario di borsa"
        subtitle={market.trades24x7 ? 'Contrattazioni continue, 24 ore su 24' : 'Tutti gli orari sono locali alla borsa'}
        flush
      >
        <TableWrap>
          <table className="table table--compact">
            <tbody>
              {/* Un mercato sempre aperto non ha orari da elencare: mostrarli
                  suggerirebbe una chiusura che non esiste. */}
              {market.trades24x7 ? (
                <tr>
                  <td>Sessione</td>
                  <td className="numeric">24/7</td>
                </tr>
              ) : (
                SCHEDULE_ROWS.map((row) => {
                  const value = market.schedule[row.key];
                  if (!value) return null;
                  return (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td className="numeric">{value}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </TableWrap>
        {market.schedule.notes && <p className="markets__notes">{market.schedule.notes}</p>}
      </Card>

      <Card title="Titoli in watchlist collegati" flush>
        {market.watchlistInstruments.length === 0 ? (
          <EmptyState
            icon="⭐"
            title="Nessun titolo collegato"
            description="Nessuno strumento in watchlist ha questo mercato come mercato di quotazione."
          />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Strumento</th>
                  <th>ISIN / Simbolo</th>
                  <th>In wishlist</th>
                </tr>
              </thead>
              <tbody>
                {market.watchlistInstruments.map((instrument) => (
                  <tr key={instrument.instrumentId}>
                    <td>{instrument.name}</td>
                    <td>{instrument.isin ?? instrument.symbol ?? '—'}</td>
                    <td>
                      {/* Un titolo monitorato e un titolo che si è già deciso di
                          comprare sono due cose diverse, e la watchlist da sola
                          non le distingue: il badge dice su quali il motore può
                          davvero operare. I portafogli sono elencati perché la
                          wishlist è per COPPIA (titolo, portafoglio) — «in
                          wishlist» senza dire dove sarebbe mezza informazione. */}
                      {wishlistByInstrument.get(instrument.instrumentId)?.length ? (
                        <Badge tone="accent">
                          {wishlistByInstrument.get(instrument.instrumentId)!.join(', ')}
                        </Badge>
                      ) : (
                        <span className="markets__muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card title="Storico briefing" subtitle="Riepiloghi AI generati prima dell'apertura" flush>
        {market.recentBriefings.length === 0 ? (
          <EmptyState icon="📰" title="Nessun briefing ancora generato" />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Stato</th>
                  <th className="numeric col--secondary">Completate</th>
                  <th className="numeric col--secondary">Fallite</th>
                  <th className="numeric col--secondary">Saltate</th>
                  <th aria-label="Azioni" />
                </tr>
              </thead>
              <tbody>
                {market.recentBriefings.map((briefing) => (
                  <tr key={briefing.id}>
                    <td>{formatDate(briefing.briefingDate)}</td>
                    <td>
                      {/* F-144: una revisione di fine giornata e un resoconto
                          del mattino stanno nella stessa tabella, e senza il
                          tipo si leggerebbero come due resoconti dello stesso
                          giorno. Il badge agentico dice COME e' stata fatta. */}
                      {BRIEFING_KIND_LABELS[briefing.kind] ?? briefing.kind}
                      {briefing.mode === 'agentic' && (
                        <>
                          {' '}
                          <AgenticBadge />
                        </>
                      )}
                    </td>
                    <td>
                      <Badge tone={BRIEFING_STATUS_TONE[briefing.status]}>{BRIEFING_STATUS_LABELS[briefing.status]}</Badge>
                    </td>
                    <td className="numeric col--secondary">{briefing.analysesCompletedCount}</td>
                    <td className="numeric col--secondary">{briefing.analysesFailedCount}</td>
                    <td className="numeric col--secondary">{briefing.analysesSkippedCount}</td>
                    <td>
                      {briefing.status === 'completed' && (
                        <Link to={`/markets/${market.mic}/briefings/${briefing.id}`}>Vedi briefing →</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
    </div>
  );
}
